import { Formik } from "formik";
import React from "react";
import { Form } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import * as yup from "yup";

import { Loader } from "../../../common/components/core";
import {
    FormContainer,
    FormFooter,
    InputField,
    SelectField,
} from "../../../common/components/form";
import { FormSubmissionHandler, BankAccount } from "../../../types/common";
import { SuperAdminRole } from "../../../types/superadmin";
import { CountryCodeOptions } from "../../../constants/countryCodes";
import { withNumberInputOnly } from "../../../common/components/form/HOC/withNumberInputOnly";
import { UserProfile } from "../../../api/profile";



export type AccountFormValues = {
    holderName: string;
    accountNumber: string;
    swiftAddress: string;
    bankName: string;
    registeredAddress: string;
    city: string;
    countryCode: string;
    paymentCurrency: string;
    profile: string;
};


const schema: yup.SchemaOf<AccountFormValues> = yup.object().shape({
    holderName: yup.string().required(),
    accountNumber: yup.string().required(),
    swiftAddress: yup.string().required(),
    bankName: yup.string().required(),
    registeredAddress: yup.string().required(),
    city: yup.string().required(),
    countryCode: yup.string().required(),
    paymentCurrency: yup.string().required(),
    profile: yup.string().required(),
});

type AccountFormProps = {
    onCancel: () => void;
    onSubmit: FormSubmissionHandler<AccountFormValues>;
    currencies: string[];
    account?: BankAccount;
    accountType: SuperAdminRole;
    profileNameList: UserProfile[];
    isEdit?: boolean;
    initialValues: {
        holderName: string;
        accountNumber: string;
        swiftAddress: string;
        bankName: string;
        registeredAddress: string;
        city: string;
        countryCode: string;
        paymentCurrency: string;
        profile: string;
    }
};

const InputNumberOnlyField = withNumberInputOnly(InputField);


export const parse2ProfileDetails = (
    userProfile: UserProfile,
): string => {
    return JSON.stringify({
        id: userProfile.id,
        contactPersonEmail: userProfile.contactPersonEmail,
        contactPersonName: userProfile.contactPersonName,
        contactPersonNumber: userProfile.contactPersonNumber,
        location: userProfile.location,
        name: userProfile.name,
        type: userProfile.type,
        accountName: userProfile.accountName,
    });
}

export const addr2Sub = (
    registeredAddress: string,
    type: string,
): string => {
    const sub = registeredAddress.split(" | ")
    switch (type) {
        case "addr" :
            if (sub && sub.length >= 3) {
                var addr = sub[0];
                for(let i=1; i <= sub.length - 3 ;i++) {
                    addr = addr.concat(" | ").concat(sub[i]);
                }
                return addr;
            } else {
                return registeredAddress;
            }
        case "city" :
            return sub && sub.length >= 3 ? sub[sub.length - 2] : "";
        case "code" :
            return sub && sub.length >= 3 ? sub[sub.length - 1] : "";
        default:
            return "";
    }
}

const AccountForm = ({
    onCancel,
    onSubmit,
    currencies,
    account,
    accountType,
    profileNameList,
    isEdit,
    initialValues,
}: AccountFormProps): JSX.Element => {
    return (
        <Formik
            validationSchema={schema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={onSubmit}
            initialValues={initialValues}
        >
            {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
                <Loader isLoading={isSubmitting}>
                    <FormContainer>
                        <h1 className="mb-3 rem-1-5">Enter Details for {`${accountType} Bank Account`}</h1>
                        <Form
                            autoComplete="off"
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <InputField
                                name="holderName"
                                label={`${accountType} Bank Account Holder Name`}
                                type="text"
                                maxLength={33}
                                onBlur={() =>
                                    setFieldValue(
                                        "holderName",
                                        values.holderName.trim(),
                                    )
                                }
                                required={true}
                            />
                            <InputNumberOnlyField
                                name="accountNumber"
                                label={`${accountType} Bank Account Number`}
                                type="text"
                                maxLength={25}
                                required={true}
                            />
                            <InputField
                                name="swiftAddress"
                                label="SWIFT Address"
                                type="text"
                                maxLength={16}
                                onBlur={() =>
                                    setFieldValue(
                                        "swiftAddress",
                                        values.swiftAddress.trim(),
                                    )
                                }
                                onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                    const value = event.target.value
                                        .toUpperCase()
                                        .replace(/[^a-zA-Z]/g, "");
                                    setFieldValue("swiftAddress", value);
                                }}
                                required={true}
                            />
                            <InputField
                                name="bankName"
                                label="Bank Name"
                                type="text"
                                maxLength={64}
                                onBlur={() =>
                                    setFieldValue(
                                        "bankName",
                                        values.bankName.trim(),
                                    )
                                }
                                required={true}
                            />
                            <InputField
                                name="registeredAddress"
                                label={`${accountType}'s Registered Address`}
                                type="text"
                                maxLength={64}
                                onBlur={() =>
                                    setFieldValue(
                                        "registeredAddress",
                                        values.registeredAddress.trim(),
                                    )
                                }
                                required={true}
                            />
                            <InputField
                                name="city"
                                label="City"
                                type="text"
                                maxLength={32}
                                required={true}
                            />
                            <SelectField
                                name="countryCode"
                                label="Country Code"
                                options={
                                    CountryCodeOptions.map((it) => ({
                                        value: it.value,
                                        label: it.label + "-" + it.value,
                                    }))
                                }
                                required={true}
                            />
                            <SelectField
                                name="paymentCurrency"
                                label="Payment Currency"
                                options={currencies.map((it) => ({
                                    value: it,
                                    label: it,
                                }))}
                                required={true}
                            />
                            <SelectField
                                name="profile"
                                label="Profile Name"
                                disabled={isEdit}
                                options={
                                    profileNameList.map((it) => ({
                                        value: parse2ProfileDetails(it),
                                        label: it.name,
                                    }))
                                }
                                required={true}
                            />
                            <div>
                                <span>IMPORTANT:</span>
                                <p>
                                    Changing the bank account details will not
                                    affect any previously created invoice
                                    submissions.
                                </p>
                            </div>

                            <FormFooter className="d-flex justify-content-between">
                                <Button
                                    variant="light.tertiary"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                                <Button variant="light.primary" type="submit">
                                    {account ? "Save" : "Create"}
                                </Button>
                            </FormFooter>
                        </Form>
                    </FormContainer>
                </Loader>
            )}
        </Formik>


    );
};

export default AccountForm;
